import { daysSince } from "@/lib/skills/catalog";
import { RISK } from "@/lib/skills/constants";
import { SORT_KEYS, type SortKey } from "@/lib/skills/search-params";
import type { SkillListItem } from "@/lib/skills/types";

export type SortDir = "asc" | "desc";

/** Narrow an arbitrary string (e.g. a TanStack column id) to a SortKey, or null. */
export function asSortKey(value: string): SortKey | null {
  return SORT_KEYS.find((k) => k === value) ?? null;
}

/** Blended default ranking, matching the prototype's relevance score. Higher = better. */
export function relevanceScore(s: SkillListItem): number {
  return (s.risk === "vetted" ? 2 : 0) + s.rating + s.downloads / 400;
}

/**
 * Ascending comparator for a sort key. Risk sorts by *rank* (vetted→high), not
 * label; "updated" by days-ago (smaller = newer); relevance by blended score.
 * Shared by the All Skills grid (preset select) and the table (TanStack sortingFns)
 * so the two can never disagree.
 */
export function compareByKey(a: SkillListItem, b: SkillListItem, key: SortKey): number {
  switch (key) {
    case "title":
      return a.title.localeCompare(b.title);
    case "cat":
      return (a.cat[0] ?? "").localeCompare(b.cat[0] ?? "");
    case "author":
      return a.authorName.localeCompare(b.authorName);
    case "risk":
      return RISK[a.risk].rank - RISK[b.risk].rank;
    case "rating":
      return a.rating - b.rating;
    case "downloads":
      return a.downloads - b.downloads;
    case "updated":
      return daysSince(a.updatedAt) - daysSince(b.updatedAt);
    case "relevance":
      return relevanceScore(a) - relevanceScore(b);
  }
}

export function applySort(skills: SkillListItem[], key: SortKey, dir: SortDir): SkillListItem[] {
  const copy = [...skills];
  copy.sort((a, b) => {
    const c = compareByKey(a, b, key);
    return dir === "asc" ? c : -c;
  });
  return copy;
}

/** Preset `<select>` value ⇄ {sort, dir} state (grid view only). */
export const SORT_PRESETS: Record<string, { sort: SortKey; dir: SortDir }> = {
  relevance: { sort: "relevance", dir: "desc" },
  downloads: { sort: "downloads", dir: "desc" },
  rating: { sort: "rating", dir: "desc" },
  recent: { sort: "updated", dir: "asc" },
  safest: { sort: "risk", dir: "asc" },
};

export const SORT_PRESET_LABELS: Record<keyof typeof SORT_PRESETS, string> = {
  relevance: "Best match",
  downloads: "Most downloaded",
  rating: "Highest rated",
  recent: "Recently updated",
  safest: "Safest first",
};

/** Find the preset key matching a {sort, dir} state, or null if it's a custom column sort. */
export function presetKeyFor(sort: SortKey, dir: SortDir): string | null {
  const found = Object.entries(SORT_PRESETS).find(([, p]) => p.sort === sort && p.dir === dir);
  return found ? found[0] : null;
}
